import React, { useContext, useEffect, useState } from 'react'
import { useHistory, useLocation, useParams } from 'react-router-dom'
import ReactGA from 'react-ga'
import moment from 'moment'

import { RootContext } from '../../../context/rootContext'
import { getPetById } from '../../../services/pet'
import { getSameProfessionalStore } from '../../../services/professionals'
import { getAppointments } from '../../../services/appointments'

import * as S from './styles'
import SideMenuTemplate from '../templates/SideMenu'
import Alert from '../../../components/Form/Alert'
import Pagination from '../../../components/Pagination'
import BaseModal from '../../../components/Modals/BaseModal'
import NewPetModal from '../components/Modals/NewPetModal'
import { DatePicker } from '../components/Form'
import { Select } from '../../../components/Form'
import HeaderPage from '../components/HeaderPage'
import ResultBox from '../components/ResultBox'
import PetInfo from '../components/PetsDataCard'
import { ReactComponent as SearchIcon } from '../../../assets/icons/search-orange.svg'
import { ReactComponent as PetAvatar } from '../../../assets/icons/pet-avatar-vet-system.svg'
import { ReactComponent as GreenPenIcon } from '../../../assets/icons/green-pen.svg'
import { ReactComponent as PlusIcon } from '../../../assets/icons/plus.svg'
import { alertConfig, LIMIT, INITIAL_CURRENT_PAGE } from './constants'
import Card from '../../../components/Card'

const PetDetails = () => {
  const { id: petId } = useParams()
  const alertParams = new URLSearchParams(useLocation().search).get('alertContent')
  const history = useHistory()
  const { globalState } = useContext(RootContext)
  const [state] = globalState
  const partnerId = state.userData.partner
  const professional = state.userData.professional
  const [professionalsInStore, setProfessionalsInStore] = useState([])
  const [isPetModalVisible, setIsPetModalVisible] = useState(false)
  const [petWasUpdated, setPetWasUpdated] = useState(false)
  const [showAlert, setShowAlert] = useState(!!alertParams)
  const [showTutorNotFoundModal, setShowTutorNotFoundModal] = useState(false)
  const [loadingPetData, setLoadingPetData] = useState(false)
  const [loadingAppointmentsData, setLoadingAppointmentsData] = useState(false)
  const [navBarSelected, setNavBarSelected] = useState('appointments')
  const [filters, setFilters] = useState({ petId, professionalId: professional.id, skip: 0, limit: 8 })
  const [appointments, setAppointments] = useState([])
  const [totalAppointments, setTotalAppointments] = useState()
  const [currentPage, setCurrentPage] = useState(INITIAL_CURRENT_PAGE)
  const [isMounted, setIsMounted] = useState(true)
  const [pet, setPet] = useState()
  const emptyTutor = !pet?.ownerId

  useEffect(() => {
    handleProfessionalsFilterOptions()
  }, [])

  const handleCloseAlert = () => {
    setShowAlert(false)
  }

  const goToPage = (url) => {
    history.push(`/${url}`)
    ReactGA.pageview(`/${url}`)
  }

  const goToTutorPage = () => {
    return pet?.ownerId ? goToPage(`vets/detalhes-do-tutor/${pet?.ownerId}`) : setShowTutorNotFoundModal(true)
  }

  const renderHeaderActions = () => {
    return <>
      <S.BoxButton>
        <S.EditButton onClick={() => {
          if (emptyTutor) {
            setShowTutorNotFoundModal(true)
            return
          }
          setShowAlert(false)
          setPetWasUpdated(false)
          setIsPetModalVisible(true)
        }}>
          <GreenPenIcon />Editar pet
        </S.EditButton>

        <S.TutorDetailsButton onClick={() => goToTutorPage()}>
          Ver ficha do tutor
        </S.TutorDetailsButton>
      </S.BoxButton>
    </>
  }

  const handleProfessionalsFilterOptions = async () => {
    const AllProfessionalsOption = { value: undefined, label: 'Todos os profissionais' }
    const sameProfessionalStore = await getSameProfessionalStore(partnerId)
    const sameProfessionalStoreOptions = sameProfessionalStore?.map((prof) => {
      return { value: prof.id, label: prof.name.substring(0, 18) + '...' }
    })
    sameProfessionalStoreOptions.unshift(AllProfessionalsOption)
    setProfessionalsInStore(sameProfessionalStoreOptions)
  }

  function noAppointmentsToDisplay () {
    return loadingAppointmentsData === false && appointments.length < 1
  }

  async function fetchPet () {
    const dataPet = await getPetById(petId)
    return { dataPet }
  }

  useEffect(() => {
    setLoadingPetData(true)

    fetchPet().then(response => {
      setPet(response.dataPet)
      setLoadingPetData(false)
    }, (error) => {
      console.log(error)
      setLoadingPetData(false)
    })
  }, [])

  useEffect(() => {
    if (petWasUpdated && !showAlert) {
      setLoadingPetData(true)
      setShowAlert(true)
      fetchPet().then(response => {
        setPet(response.dataPet)
        setLoadingPetData(false)
      }, (error) => {
        console.log(error)
        setLoadingPetData(false)
      })
    }
  }, [petWasUpdated])

  useEffect(() => {
    setIsMounted(true)
    setLoadingAppointmentsData(true)

    async function fetchAppointments () {
      try {
        const appointmentsFilter = { ...filters, skip: (currentPage - 1) * LIMIT }

        if (filters.date) {
          const dateObject = moment(filters.date)
          const formattedDate = dateObject.format('DD-MM-YYYY')
          appointmentsFilter.date = formattedDate.toString()
        }

        Object.keys(appointmentsFilter).forEach(key => {
          if (!appointmentsFilter[key]) {
            delete appointmentsFilter[key]
          }
        })

        const response = await getAppointments({ ...appointmentsFilter })

        if (isMounted) {
          setAppointments(response.list)
          setTotalAppointments(response.total)
          setLoadingAppointmentsData(false)
        }
      } catch (error) {
        setLoadingAppointmentsData(false)
      }
    }

    fetchAppointments()
    return () => setIsMounted(false)
  }, [filters, currentPage, isMounted])

  function alertContent () {
    if (petWasUpdated) { return 'PET_UPDATED' }
    if (alertParams) { return alertParams }
  }

  return <>
    <SideMenuTemplate>
      <S.MainContainer>
        <S.Main>
          <Alert type={alertConfig[alertContent()]?.icon} show={showAlert} onClose={handleCloseAlert}>
            {alertConfig[alertContent()]?.textContent}
          </Alert>
          <S.HeaderWrapper>
            <HeaderPage style={{ marginBottom: 10 }}
              icon={ <PetAvatar /> }
              iconBoxStyle={{ padding: 0 }}
              title={`${pet?.name}, ${pet?.breed?.name || 'Não preenchido'}`}
              sub={[
                `Tutor: ${pet?.ownerName || 'Não preenchido'}`
              ]}
              loading={loadingPetData}
              action={renderHeaderActions()}
            />
            <Card full loading={loadingPetData} skeletonHeight={76} padding={'0'} hasBorder={false}>
              <PetInfo hasTopBorder data={pet} />
            </Card>
          </S.HeaderWrapper>
          <S.AppointmentsWrapper>
            <S.AppointmentNavBar>
              <S.AppointmentNavBarTitle active={navBarSelected === 'appointments'}>Fichas de consultas</S.AppointmentNavBarTitle>
              <S.AppointmentNavBarTitle active={navBarSelected === 'vaccines'}>Vacinas</S.AppointmentNavBarTitle>
              <S.AppointmentNavBarTitle active={navBarSelected === 'prescriptions'}>Prescrições</S.AppointmentNavBarTitle>
              <S.AppointmentNavBarTitle active={navBarSelected === 'exams'}>Solicitações de exames</S.AppointmentNavBarTitle>
            </S.AppointmentNavBar>
            <S.AppointmentActions>
              <S.SelectGroup>
                <Select
                  emptyLabel='Todos os profissionais'
                  options={professionalsInStore}
                  selectedValue={professional.id}
                  onChange={(professionalId) => setFilters(prevState => ({
                    ...prevState,
                    professionalId: professionalId
                  }))}
                />

                <DatePicker showIcon={!filters.date}
                  invertIcon
                  width={'178px'}
                  height={'40px'}
                  showYearDropdown
                  showMonthDropdown
                  hideCurrentMonth
                  isClearable={filters.date}
                  selected={filters.date && moment(filters.date).toDate()}
                  locale='ptBR'
                  className='form-control'
                  placeholderText='Selecione uma data'
                  dateFormat='dd/MM/yyyy'
                  onChange={(date) => setFilters(prevState => ({
                    ...prevState,
                    date: date
                  }))}
                />

              </S.SelectGroup>

              <S.NewAppointmentButton onClick={() => goToPage(`vets/prontuario?petId=${petId}`)}>
                <PlusIcon />
                Nova ficha de consulta
              </S.NewAppointmentButton>
            </S.AppointmentActions>
            <ResultBox
              resultFor='appointments'
              data={appointments}
              showItemsCount={false}
              loading={loadingAppointmentsData}
              limit={LIMIT}
              currentPage={currentPage}
              totalResults={totalAppointments}
            />
            {noAppointmentsToDisplay() && (
              <S.Body>
                <S.IconWrapper>
                  <SearchIcon />
                </S.IconWrapper>
                <S.TextWrapper>
                  <S.TitlePets>Nenhuma consulta registrada no momento</S.TitlePets>
                  <S.SubtitlePets>Inicie uma nova ficha clicando em “Nova ficha de consulta”</S.SubtitlePets>
                </S.TextWrapper>
              </S.Body>
            )}
          </S.AppointmentsWrapper>
          <Pagination
            totalItems={totalAppointments}
            itemsPerPage={LIMIT}
            currentPage={currentPage}
            setCurrentPage={setCurrentPage}
            show={!loadingAppointmentsData && !!totalAppointments}
          />
        </S.Main>
      </S.MainContainer>
      <BaseModal
        visibility={showTutorNotFoundModal}
        setVisibility={setShowTutorNotFoundModal}
        icon={<SearchIcon />}
        title='Ficha do tutor não encontrada'
        subTitle='Este pet foi cadastrado sem os dados do tutor. Por favor, verifique o que pode ter acontecido diretamente com a recepção. Indicamos que, caso o registro esteja incorreto, inicie um novo cadastro.'>
        <S.CloseModalButton onClick={() => setShowTutorNotFoundModal(false)}>
          Fechar
        </S.CloseModalButton>
      </BaseModal>
      <NewPetModal petData={pet} visibility={isPetModalVisible} setVisibility={setIsPetModalVisible} updated={setPetWasUpdated} />
    </SideMenuTemplate>
  </>
}
export default PetDetails
